export const bare = (jid: string | undefined) => (jid ?? "").split("/")[0];
export const localpart = (jid: string) => jid.split("@")[0];
export const phoneOf = (jid: string) => "+" + localpart(jid);

export function uuid(): string {
  return crypto.randomUUID();
}

const DAY = 86_400_000;

function startOfDay(ts: number) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export const timeOf = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** Chat list timestamp: time today, "Yesterday", weekday, or date. */
export function listTime(ts: number): string {
  if (!ts) return "";
  const diff = startOfDay(Date.now()) - startOfDay(ts);
  if (diff <= 0) return timeOf(ts);
  if (diff <= DAY) return "Yesterday";
  if (diff < 7 * DAY) return new Date(ts).toLocaleDateString([], { weekday: "long" });
  return new Date(ts).toLocaleDateString();
}

/** Separator label between days in a conversation. */
export function dayLabel(ts: number): string {
  const diff = startOfDay(Date.now()) - startOfDay(ts);
  if (diff <= 0) return "Today";
  if (diff <= DAY) return "Yesterday";
  if (diff < 7 * DAY) return new Date(ts).toLocaleDateString([], { weekday: "long" });
  return new Date(ts).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" });
}

export const sameDay = (a: number, b: number) => startOfDay(a) === startOfDay(b);

export function lastSeenLabel(ts: number): string {
  const diff = startOfDay(Date.now()) - startOfDay(ts);
  if (diff <= 0) return `last seen today at ${timeOf(ts)}`;
  if (diff <= DAY) return `last seen yesterday at ${timeOf(ts)}`;
  return `last seen ${new Date(ts).toLocaleDateString()} at ${timeOf(ts)}`;
}

export function initials(name: string): string {
  // "~Ann" (unsaved contact) -> "A"
  const parts = name.replace(/^[+~@]+/, "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (/^\d/.test(parts[0])) return "#";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Stable pastel color per JID for avatars without a photo. */
export function colorFor(jid: string): string {
  let h = 0;
  for (const c of jid) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return `hsl(${h % 360} 45% 55%)`;
}

/** Text without WhatsApp-style formatting markers (ui/format.tsx), for the
 * chat list and notifications: "*hi* _there_" -> "hi there". */
export function plainText(text: string): string {
  return text
    .replace(/```([\s\S]+?)```/g, "$1")
    .replace(/(^|[\s\p{P}\p{S}])([*_~`])(?=\S)([^\n]*?\S)\2(?=$|[\s\p{P}\p{S}])/gmu, "$1$3")
    .replace(/^> /gm, "");
}
